import { useCallback } from 'react';

import { di } from 'react-magnetic-di';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { fireUIAnalytics } from '@atlassian/analytics-bridge/fire-analytics/fire-analytics';

const SOLUTION_CREATE_FLOW_VIEWED_EVENT = 'solutionCreateFlow viewed';
const SOLUTION_CREATE_FLOW_STARTED_EVENT = 'solutionCreateFlow started';
const ANALYTICS_TAGS = ['atlaskit'];
const ANALYTICS_OBJECT_TYPE = 'skill';

export type SkillCreateFlowTouchPoint =
	| 'manualCreationLinks'
	| 'studioUniversalCreate'
	| 'studioCustomSkillsPage'
	| 'chatImportSkillButton'
	| 'chatSkillListBuildSkill'
	| 'chatAutoSuggestBuildSkill'
	| 'chatTypeAheadBuildSkill'
	| 'chatCreateSkillWithChat'
	| 'chatCreateSkillWithEditor';

export const useCustomSkillsAnalytics = (): {
	fireSkillCreateFlowViewed: (touchPoint: SkillCreateFlowTouchPoint) => void;
	fireSkillCreateFlowStart: (touchPoint: SkillCreateFlowTouchPoint) => void;
} => {
	di(useAnalyticsEvents, fireUIAnalytics);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const fireSkillCreateFlowEvent = useCallback(
		(eventName: string, touchPoint: SkillCreateFlowTouchPoint) => {
			const event = createAnalyticsEvent({});
			// The analytics bridge reads `tags` from the event context, not from the payload, so
			// setting them on the payload would silently drop them before the event is sent.
			event.context = [...event.context, { tags: ANALYTICS_TAGS }];
			fireUIAnalytics(event, eventName, {
				objectType: ANALYTICS_OBJECT_TYPE,
				touchPoint,
			});
		},
		[createAnalyticsEvent],
	);

	const fireSkillCreateFlowViewed = useCallback(
		(touchPoint: SkillCreateFlowTouchPoint) => {
			fireSkillCreateFlowEvent(SOLUTION_CREATE_FLOW_VIEWED_EVENT, touchPoint);
		},
		[fireSkillCreateFlowEvent],
	);

	const fireSkillCreateFlowStart = useCallback(
		(touchPoint: SkillCreateFlowTouchPoint) => {
			fireSkillCreateFlowEvent(SOLUTION_CREATE_FLOW_STARTED_EVENT, touchPoint);
		},
		[fireSkillCreateFlowEvent],
	);

	return { fireSkillCreateFlowViewed, fireSkillCreateFlowStart };
};
